import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useAuth } from "../auth/AuthContext";
import { SearchBar } from "../components/SearchBar";
import { Pagination } from "../components/Pagination";
import SelectScenarios from "../components/SelectScenarios";

const STORAGE_KEYS = {
  rootTab: "admin_pathways_scenarios_tab_v1",
  pathwaysSearchInput: "admin_pathways_search_input_v1",
  pathwaysSearch: "admin_pathways_search_v1",
  pathwaysPage: "admin_pathways_page_v1",
  scenariosSearchInput: "admin_scenarios_search_input_v1",
  scenariosSearch: "admin_scenarios_search_v1",
  scenariosType: "admin_scenarios_type_filter_v1",
  scenariosPage: "admin_scenarios_page_v1",
};

const SCENARIO_TYPES = [
  { value: "Diet", label: "Diet" },
  { value: "Imports", label: "Imports" },
  { value: "Exports", label: "Exports" },
  { value: "ProtectedAreas", label: "PA" },
  { value: "Afforestation", label: "Afforestation" },
];

function TabBtn({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
        active
          ? "bg-[#03203d] text-white shadow-sm"
          : "bg-white text-slate-600 hover:text-slate-900"
      }`}
    >
      {label}
    </button>
  );
}

function validateJson(text) {
  const raw = String(text || "").trim();
  if (!raw) return { valid: true, empty: true };
  try {
    JSON.parse(raw);
    return { valid: true, empty: false };
  } catch (e) {
    return { valid: false, error: e?.message };
  }
}

function prettyJson(value) {
  const raw = typeof value === "string" ? value : JSON.stringify(value ?? "");
  try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
}

function sortJsonDeep(value) {
  if (Array.isArray(value)) return value.map(sortJsonDeep);
  if (value && typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce((acc, key) => {
        acc[key] = sortJsonDeep(value[key]);
        return acc;
      }, {});
  }
  return value;
}

function isJsonEquivalent(a, b) {
  try {
    return JSON.stringify(sortJsonDeep(a)) === JSON.stringify(sortJsonDeep(b));
  } catch {
    return false;
  }
}

function readStoredString(key, fallback = "") {
  try {
    const value = localStorage.getItem(key);
    return value ?? fallback;
  } catch {
    return fallback;
  }
}

function readStoredPositiveInt(key, fallback = 1) {
  try {
    const raw = localStorage.getItem(key);
    const num = Number(raw);
    return Number.isInteger(num) && num > 0 ? num : fallback;
  } catch {
    return fallback;
  }
}

function writeStoredValue(key, value) {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // ignore storage errors
  }
}

function JsonEditor({ value, onChange, isValid, error, onFormat, onMinify }) {
  const lineCount = Math.max(1, String(value || "").split("\n").length);
  const lineNumbers = Array.from({ length: lineCount }, (_, i) => i + 1).join("\n");

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onFormat}
          className="rounded bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700"
        >
          Format JSON
        </button>
        <button
          type="button"
          onClick={onMinify}
          className="rounded bg-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-300"
        >
          Minify
        </button>
        <span className={`text-xs font-medium ${isValid ? "text-emerald-700" : "text-red-700"}`}>
          {isValid ? "Valid JSON" : error || "Invalid JSON"}
        </span>
      </div>

      <div className="grid grid-cols-[auto,1fr] overflow-hidden rounded border border-slate-300 bg-white">
        <pre className="m-0 select-none border-r border-slate-200 bg-slate-50 px-2 py-2 text-right text-xs text-slate-400 font-mono leading-5">
          {lineNumbers}
        </pre>
        <textarea
          className="w-full min-h-48 resize-y border-0 px-3 py-2 text-sm font-mono leading-5 outline-none"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={false}
        />
      </div>
    </div>
  );
}

function DetailModal({ title, open, onClose, children }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-3xl rounded-2xl bg-white shadow-xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none">×</button>
        </div>
        <div className="overflow-y-auto px-6 py-4 space-y-4 flex-1">{children}</div>
        <div className="flex justify-end px-6 py-4 border-t border-slate-200">
          <button onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">Close</button>
        </div>
      </div>
    </div>
  );
}

function ReadOnlyField({ label, value }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 break-words">
        {value || "—"}
      </div>
    </div>
  );
}

function ReadOnlyJson({ label, value }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
      <pre className="max-h-[50vh] overflow-auto rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs leading-5 text-slate-800">
        {prettyJson(value)}
      </pre>
    </div>
  );
}

// ─── PATHWAYS ────────────────────────────────────────────────────────────────

function PathwaysTab() {
  const { authFetch } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState(() =>
    readStoredString(STORAGE_KEYS.pathwaysSearchInput, ""),
  );
  const [search, setSearch] = useState(() =>
    readStoredString(STORAGE_KEYS.pathwaysSearch, ""),
  );
  const [page, setPage] = useState(() =>
    readStoredPositiveInt(STORAGE_KEYS.pathwaysPage, 1),
  );
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const LIMIT = 20;

  const [editId, setEditId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [working, setWorking] = useState(null);
  const [viewRecord, setViewRecord] = useState(null);

  const jsonState = useMemo(() => validateJson(editValues.pathway), [editValues.pathway]);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); setSearch(searchInput.trim()); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    writeStoredValue(STORAGE_KEYS.pathwaysSearchInput, searchInput);
  }, [searchInput]);

  useEffect(() => {
    writeStoredValue(STORAGE_KEYS.pathwaysSearch, search);
  }, [search]);

  useEffect(() => {
    writeStoredValue(STORAGE_KEYS.pathwaysPage, page);
  }, [page]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ page: String(page), limit: String(LIMIT) });
    if (search) params.set("search", search);
    authFetch(`/api/pathways/admin/pathways?${params}`)
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d?.error || `HTTP ${r.status}`);
        return d;
      })
      .then((d) => {
        if (!alive) return;
        setRows(Array.isArray(d.rows) ? d.rows : []);
        setTotal(Number(d.total) || 0);
        setTotalPages(Math.max(1, Math.ceil((Number(d.total) || 0) / LIMIT)));
      })
      .catch((e) => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [authFetch, page, search]);

  const openEdit = async (row) => {
    setWorking(row.id);
    try {
      const res = await authFetch(`/api/pathways/admin/pathways/${row.id}`);
      const data = await res.json();
      setEditId(row.id);
      setEditValues({
        name: data.name ?? "",
        description: data.description ?? "",
        pathway: prettyJson(data.pathway),
      });
    } catch (e) {
      await Swal.fire({ icon: "error", title: "Error", text: e.message });
    } finally {
      setWorking(null);
    }
  };

  const openView = async (row) => {
    setWorking(row.id);
    try {
      const res = await authFetch(`/api/pathways/admin/pathways/${row.id}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      setViewRecord({
        id: data.id ?? row.id,
        country: data.country ?? row.country,
        name: data.name ?? row.name,
        description: data.description ?? row.description,
        created_at: data.created_at ?? row.created_at,
        updated_at: data.updated_at ?? row.updated_at,
        pathway: data.pathway ?? {},
      });
    } catch (e) {
      await Swal.fire({ icon: "error", title: "Error", text: e.message });
    } finally {
      setWorking(null);
    }
  };

  const handleSave = async () => {
    if (!jsonState.valid) {
      await Swal.fire({ icon: "warning", title: "Invalid JSON", text: jsonState.error });
      return;
    }
    setWorking(editId);
    try {
      const res = await authFetch(`/api/pathways/admin/pathways/${editId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: editValues.name,
          description: editValues.description,
          pathway: String(editValues.pathway || "").trim() ? JSON.parse(editValues.pathway) : {},
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      setRows((prev) => prev.map((r) => r.id === editId ? { ...r, name: data.row.name, description: data.row.description, updated_at: data.row.updated_at } : r));
      setEditId(null);
      await Swal.fire({ icon: "success", title: "Saved", timer: 1500, showConfirmButton: false });
    } catch (e) {
      await Swal.fire({ icon: "error", title: "Error", text: e.message });
    } finally {
      setWorking(null);
    }
  };

  const handleDelete = async (id) => {
    const { isConfirmed } = await Swal.fire({
      icon: "warning", title: "Delete pathway?", text: `ID #${id}`,
      showCancelButton: true, confirmButtonText: "Delete", confirmButtonColor: "#d33",
    });
    if (!isConfirmed) return;
    setWorking(id);
    try {
      const res = await authFetch(`/api/pathways/admin/pathways/${id}`, { method: "DELETE" });
      if (!res.ok) { const d = await res.json(); throw new Error(d?.error || `HTTP ${res.status}`); }
      setRows((prev) => prev.filter((r) => r.id !== id));
      setTotal((n) => Math.max(0, n - 1));
      await Swal.fire({ icon: "success", title: "Deleted", timer: 1500, showConfirmButton: false });
    } catch (e) {
      await Swal.fire({ icon: "error", title: "Error", text: e.message });
    } finally {
      setWorking(null);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-500"><span className="font-semibold text-slate-800">{total}</span> pathways</p>
        <SearchBar value={searchInput} onChange={setSearchInput} placeholder="Search by name or country..." />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-[#03203d]" /></div>
      ) : error ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">{error}</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-sm text-slate-500">No pathways found.</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                {["ID", "Country", "Name", "Description", "Updated", "Actions"].map((h) => (
                  <th key={h} className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.id}
                  onClick={() => openView(row)}
                  className={`cursor-pointer border-b border-slate-100 ${i % 2 === 0 ? "bg-white" : "bg-slate-50/50"} hover:bg-blue-50/30`}
                >
                  <td className="px-4 py-2 text-slate-600">{row.id}</td>
                  <td className="px-4 py-2 font-medium text-slate-800">{row.country}</td>
                  <td className="px-4 py-2 text-slate-700">{row.name}</td>
                  <td className="px-4 py-2 text-slate-500 max-w-xs truncate">{row.description || "—"}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-slate-500 text-xs">{row.updated_at ? new Date(row.updated_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-2">
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEdit(row);
                        }}
                        disabled={working === row.id}
                        className="rounded-lg bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                      >
                        {working === row.id ? "..." : "Edit"}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(row.id);
                        }}
                        disabled={working === row.id}
                        className="rounded-lg bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {/* Edit modal */}
      {editId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900">Edit Pathway #{editId}</h3>
              <button onClick={() => setEditId(null)} className="text-slate-400 hover:text-slate-700 text-xl leading-none">×</button>
            </div>
            <div className="overflow-y-auto px-6 py-4 space-y-4 flex-1">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Name</label>
                <input
                  value={editValues.name}
                  onChange={(e) => setEditValues((p) => ({ ...p, name: e.target.value }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Description</label>
                <input
                  value={editValues.description}
                  onChange={(e) => setEditValues((p) => ({ ...p, description: e.target.value }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-slate-600">Pathway JSON</label>
                  {!jsonState.valid && <span className="text-xs text-red-500">{jsonState.error}</span>}
                </div>
                <textarea
                  rows={14}
                  value={editValues.pathway}
                  onChange={(e) => setEditValues((p) => ({ ...p, pathway: e.target.value }))}
                  className={`w-full rounded-lg border px-3 py-2 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 ${jsonState.valid ? "border-slate-200" : "border-red-400 bg-red-50"}`}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
              <button onClick={() => setEditId(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
              <button
                onClick={handleSave}
                disabled={!jsonState.valid || working === editId}
                className="rounded-lg bg-[#03203d] px-4 py-2 text-sm font-medium text-white hover:bg-[#05407b] disabled:opacity-50"
              >
                {working === editId ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      <DetailModal
        open={!!viewRecord}
        onClose={() => setViewRecord(null)}
        title={viewRecord ? `Pathway #${viewRecord.id} details` : "Pathway details"}
      >
        {viewRecord && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ReadOnlyField label="ID" value={String(viewRecord.id)} />
              <ReadOnlyField label="Country" value={viewRecord.country} />
              <ReadOnlyField label="Name" value={viewRecord.name} />
              <ReadOnlyField
                label="Updated"
                value={viewRecord.updated_at ? new Date(viewRecord.updated_at).toLocaleString() : "—"}
              />
            </div>
            <ReadOnlyField label="Description" value={viewRecord.description} />
            <ReadOnlyJson label="Pathway JSON" value={viewRecord.pathway} />
          </>
        )}
      </DetailModal>
    </div>
  );
}

// ─── SCENARIOS ───────────────────────────────────────────────────────────────

function ScenariosTab() {
  const { authFetch } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState(() =>
    readStoredString(STORAGE_KEYS.scenariosSearchInput, ""),
  );
  const [search, setSearch] = useState(() =>
    readStoredString(STORAGE_KEYS.scenariosSearch, ""),
  );
  const [typeFilter, setTypeFilter] = useState(() =>
    readStoredString(STORAGE_KEYS.scenariosType, ""),
  );
  const [page, setPage] = useState(() =>
    readStoredPositiveInt(STORAGE_KEYS.scenariosPage, 1),
  );
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const LIMIT = 20;

  const [editId, setEditId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [working, setWorking] = useState(null);
  const [viewRecord, setViewRecord] = useState(null);

  const jsonState = useMemo(() => validateJson(editValues.config), [editValues.config]);

  const handleFormatJson = () => {
    if (!jsonState.valid) return;
    try {
      setEditValues((prev) => ({
        ...prev,
        config: JSON.stringify(JSON.parse(String(prev.config || "")), null, 2),
      }));
    } catch {
      // no-op
    }
  };

  const handleMinifyJson = () => {
    if (!jsonState.valid) return;
    try {
      setEditValues((prev) => ({
        ...prev,
        config: JSON.stringify(JSON.parse(String(prev.config || ""))),
      }));
    } catch {
      // no-op
    }
  };

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); setSearch(searchInput.trim()); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    writeStoredValue(STORAGE_KEYS.scenariosSearchInput, searchInput);
  }, [searchInput]);

  useEffect(() => {
    writeStoredValue(STORAGE_KEYS.scenariosSearch, search);
  }, [search]);

  useEffect(() => {
    writeStoredValue(STORAGE_KEYS.scenariosType, typeFilter);
  }, [typeFilter]);

  useEffect(() => {
    writeStoredValue(STORAGE_KEYS.scenariosPage, page);
  }, [page]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ page: String(page), limit: String(LIMIT) });
    if (search) params.set("search", search);
    if (typeFilter) params.set("type_scenario", typeFilter);
    authFetch(`/api/scenarios/admin/scenarios?${params}`)
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d?.error || `HTTP ${r.status}`);
        return d;
      })
      .then((d) => {
        if (!alive) return;
        setRows(Array.isArray(d.rows) ? d.rows : []);
        setTotal(Number(d.total) || 0);
        setTotalPages(Math.max(1, Math.ceil((Number(d.total) || 0) / LIMIT)));
      })
      .catch((e) => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [authFetch, page, search, typeFilter]);

  const openEdit = async (row) => {
    setWorking(row.id);
    try {
      const res = await authFetch(`/api/scenarios/admin/scenarios/${row.id}?_=${Date.now()}`);
      if (!res.ok) {
        const msg = await res.json().catch(() => ({}));
        throw new Error(msg?.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setEditId(row.id);
      setEditValues({
        name: data.name ?? "",
        implementation_timing: data.implementation_timing ?? "",
        config: prettyJson(data.config),
        type_scenario: data.type_scenario ?? "",
      });
    } catch (e) {
      await Swal.fire({ icon: "error", title: "Error", text: e.message });
    } finally {
      setWorking(null);
    }
  };

  const openView = async (row) => {
    setWorking(row.id);
    try {
      const res = await authFetch(`/api/scenarios/admin/scenarios/${row.id}?_=${Date.now()}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      setViewRecord({
        id: data.id ?? row.id,
        country: data.country ?? row.country,
        name: data.name ?? row.name,
        type_scenario: data.type_scenario ?? row.type_scenario,
        implementation_timing: data.implementation_timing ?? row.implementation_timing,
        created_at: data.created_at ?? row.created_at,
        updated_at: data.updated_at ?? row.updated_at,
        config: data.config ?? {},
      });
    } catch (e) {
      await Swal.fire({ icon: "error", title: "Error", text: e.message });
    } finally {
      setWorking(null);
    }
  };

  const handleSave = async () => {
    if (!jsonState.valid) {
      await Swal.fire({ icon: "warning", title: "Invalid JSON", text: jsonState.error });
      return;
    }
    setWorking(editId);
    try {
      const res = await authFetch(`/api/scenarios/admin/scenarios/${editId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: editValues.name,
          implementation_timing: editValues.implementation_timing || null,
          config: String(editValues.config || "").trim() ? JSON.parse(editValues.config) : {},
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);

      // Read-after-write check to avoid reporting false positives in UI.
      const verifyRes = await authFetch(`/api/scenarios/admin/scenarios/${editId}?_=${Date.now()}`);
      const verifyData = await verifyRes.json().catch(() => ({}));
      if (!verifyRes.ok) {
        throw new Error(verifyData?.error || `Verify failed (${verifyRes.status})`);
      }

      const requested = String(editValues.config || "").trim() ? JSON.parse(editValues.config) : {};
      const saved = verifyData?.config ?? null;
      if (saved === null || !isJsonEquivalent(requested, saved)) {
        throw new Error("The server acknowledged save, but config did not persist as expected.");
      }

      setRows((prev) => prev.map((r) => (r.id === editId ? { ...r, ...verifyData } : r)));
      setEditId(null);
      await Swal.fire({ icon: "success", title: "Saved", timer: 1500, showConfirmButton: false });
    } catch (e) {
      await Swal.fire({ icon: "error", title: "Error", text: e.message });
    } finally {
      setWorking(null);
    }
  };

  const handleDelete = async (id) => {
    const { isConfirmed } = await Swal.fire({
      icon: "warning", title: "Delete scenario?", text: `ID #${id}`,
      showCancelButton: true, confirmButtonText: "Delete", confirmButtonColor: "#d33",
    });
    if (!isConfirmed) return;
    setWorking(id);
    try {
      const res = await authFetch(`/api/scenarios/admin/scenarios/${id}`, { method: "DELETE" });
      if (!res.ok) { const d = await res.json(); throw new Error(d?.error || `HTTP ${res.status}`); }
      setRows((prev) => prev.filter((r) => r.id !== id));
      setTotal((n) => Math.max(0, n - 1));
      await Swal.fire({ icon: "success", title: "Deleted", timer: 1500, showConfirmButton: false });
    } catch (e) {
      await Swal.fire({ icon: "error", title: "Error", text: e.message });
    } finally {
      setWorking(null);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <p className="text-sm text-slate-500 lg:shrink-0"><span className="font-semibold text-slate-800">{total}</span> scenarios</p>
        <div className="w-full lg:flex-1">
          <SearchBar value={searchInput} onChange={setSearchInput} placeholder="Search by name or country..." />
        </div>
        <div className="w-full sm:w-56 lg:shrink-0">
          <SelectScenarios
            value={typeFilter}
            onChange={(next) => { setTypeFilter(String(next || "")); setPage(1); }}
            options={[{ value: "", label: "All types" }, ...SCENARIO_TYPES]}
            placeholder="All types"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-[#03203d]" /></div>
      ) : error ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">{error}</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-sm text-slate-500">No scenarios found.</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                {["ID", "Country", "Name", "Type", "Timing", "Created", "Actions"].map((h) => (
                  <th key={h} className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.id}
                  onClick={() => openView(row)}
                  className={`cursor-pointer border-b border-slate-100 ${i % 2 === 0 ? "bg-white" : "bg-slate-50/50"} hover:bg-blue-50/30`}
                >
                  <td className="px-4 py-2 text-slate-600">{row.id}</td>
                  <td className="px-4 py-2 font-medium text-slate-800">{row.country}</td>
                  <td className="px-4 py-2 text-slate-700">{row.name}</td>
                  <td className="px-4 py-2">
                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">{row.type_scenario}</span>
                  </td>
                  <td className="px-4 py-2 text-slate-500 text-xs">{row.implementation_timing || "—"}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-slate-500 text-xs">{row.created_at ? new Date(row.created_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-2">
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEdit(row);
                        }}
                        disabled={working === row.id}
                        className="rounded-lg bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                      >
                        {working === row.id ? "..." : "Edit"}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(row.id);
                        }}
                        disabled={working === row.id}
                        className="rounded-lg bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {/* Edit modal */}
      {editId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900">
                Edit Scenario #{editId} <span className="text-slate-400 font-normal">({editValues.type_scenario})</span>
              </h3>
              <button onClick={() => setEditId(null)} className="text-slate-400 hover:text-slate-700 text-xl leading-none">×</button>
            </div>
            <div className="overflow-y-auto px-6 py-4 space-y-4 flex-1">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Name</label>
                <input
                  value={editValues.name}
                  onChange={(e) => setEditValues((p) => ({ ...p, name: e.target.value }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Implementation Timing</label>
                <input
                  value={editValues.implementation_timing}
                  onChange={(e) => setEditValues((p) => ({ ...p, implementation_timing: e.target.value }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Linear, Delayed"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-slate-600">Config JSON</label>
                </div>
                <JsonEditor
                  value={editValues.config}
                  onChange={(next) => setEditValues((p) => ({ ...p, config: next }))}
                  isValid={jsonState.valid}
                  error={jsonState.error}
                  onFormat={handleFormatJson}
                  onMinify={handleMinifyJson}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
              <button onClick={() => setEditId(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
              <button
                onClick={handleSave}
                disabled={!jsonState.valid || working === editId}
                className="rounded-lg bg-[#03203d] px-4 py-2 text-sm font-medium text-white hover:bg-[#05407b] disabled:opacity-50"
              >
                {working === editId ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      <DetailModal
        open={!!viewRecord}
        onClose={() => setViewRecord(null)}
        title={viewRecord ? `Scenario #${viewRecord.id} details` : "Scenario details"}
      >
        {viewRecord && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ReadOnlyField label="ID" value={String(viewRecord.id)} />
              <ReadOnlyField label="Country" value={viewRecord.country} />
              <ReadOnlyField label="Name" value={viewRecord.name} />
              <ReadOnlyField label="Type" value={viewRecord.type_scenario} />
              <ReadOnlyField label="Implementation Timing" value={viewRecord.implementation_timing} />
              <ReadOnlyField
                label="Updated"
                value={viewRecord.updated_at ? new Date(viewRecord.updated_at).toLocaleString() : "—"}
              />
            </div>
            <ReadOnlyJson label="Config JSON" value={viewRecord.config} />
          </>
        )}
      </DetailModal>
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────

export default function PathwaysScenariosAdmin() {
  const [tab, setTab] = useState(() => {
    const saved = readStoredString(STORAGE_KEYS.rootTab, "pathways");
    return saved === "scenarios" ? "scenarios" : "pathways";
  });

  useEffect(() => {
    writeStoredValue(STORAGE_KEYS.rootTab, tab);
  }, [tab]);

  return (
    <div className="w-full p-4 md:p-6 font-montserrat space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Pathways & Scenarios</h2>
        <p className="text-gray-600">Review and maintain the pathway and scenario catalogs used by Scenathon.</p>
      </div>

      <div className="inline-flex w-fit rounded-xl bg-slate-100 p-1">
        <TabBtn label="Pathways" active={tab === "pathways"} onClick={() => setTab("pathways")} />
        <TabBtn label="Scenarios" active={tab === "scenarios"} onClick={() => setTab("scenarios")} />
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {tab === "pathways" ? <PathwaysTab /> : <ScenariosTab />}
      </div>
    </div>
  );
}
